<script setup lang="ts">
import {computed} from 'vue';
import {
  type BalmSidebarMenuItem,
  BalmSidebarType,
} from '@/ts/types/component/balm-sidebar';
import FlatIconBtn from '@/vue/molecules/flat-icon-btn.vue';
import {useDrawerStore} from '@/ts/store/drawer-store';
import {storeToRefs} from 'pinia';
import SidebarTooltip from '@/vue/atoms/sidebar-tooltip.vue';
import {useTourStore} from '@/ts/store/tour-store';
import {TOUR_NAMES} from '@/ts/utils/tour-constants';
import {useRoute} from 'vue-router';
import {LANGUAGE_REGEX} from '@/ts/utils/consts';
import SvgIllustration from '@/vue/atoms/svg-illustration.vue';
import {useLegalDocsHint} from '@/ts/composables/stateful/use-legal-docs-hint';

const LEGAL_DOCS_HINT_AUTO_DISMISS_MS = 5000;

const drawerStore = useDrawerStore();
const {balmSidebarState} = storeToRefs(drawerStore);
const tourStore = useTourStore();
const route = useRoute();
const {shouldShow: showLegalDocsHint, dismiss: dismissLegalDocsHint} = useLegalDocsHint();

defineProps<{
  sidebarMenuItems: BalmSidebarMenuItem[];
}>();

function onClickItem(type: BalmSidebarType): void {
  drawerStore.openBalmSidebarDrawer(type);
}

// Check if onboarding tour is available for current route
const isTourAvailable = computed(() => {
  const pathWithoutLang = route.path.replace(LANGUAGE_REGEX.PATH_WITH_LANG, '/');
  return tourStore.isTourAvailableForPath(TOUR_NAMES.onboardingTour, pathWithoutLang);
});

// Check if tour has not been seen (show badge)
const showTourBadge = computed(() => {
  return tourStore.shouldShowTour(TOUR_NAMES.onboardingTour);
});

// Handle tour icon click
function onClickTourIcon(): void {
  tourStore.triggerTour(TOUR_NAMES.onboardingTour);
}
</script>
<template>
  <div class="balm-sidebar-toolbar">
    <div class="balm-sidebar-toolbar__items js-sidebar-toolbar" role="toolbar">
      <div
        v-for="item of sidebarMenuItems"
        :key="item.type"
        class="sidebaritem"
        :class="`js-sidebar-item-${item.type}`"
      >
        <sidebar-tooltip
          v-if="item.type === BalmSidebarType.IMPRINT && showLegalDocsHint"
          :hide="!balmSidebarState"
          :label="$t('legal_documents')"
          :auto-dismiss-ms="LEGAL_DOCS_HINT_AUTO_DISMISS_MS"
          @dismiss="dismissLegalDocsHint"
        />
        <flat-icon-btn
          :class="`js-sidebar-btn-${item.type}`"
          :icon="item.icon"
          :label="$t(item.labelKey)"
          :active="balmSidebarState === item.type"
          :use-label-as-title-attribute="true"
          padded
          @click="onClickItem(item.type)"
        />
      </div>
    </div>

    <!-- Tour icon at bottom (only when sidebar is closed and tour is available) -->
    <button
      v-if="isTourAvailable && !balmSidebarState"
      class="tour-icon-btn"
      :title="$t('product_tour')"
      @click="onClickTourIcon"
    >
      <svg-illustration name="cursor-box.svg"/>
      <span v-if="showTourBadge" class="tour-badge"/>
    </button>
  </div>
</template>
<style scoped lang="scss">
.balm-sidebar-toolbar {
  padding: var(--Spacing-spacing-5) 0 var(--Spacing-spacing-1);
  display: flex;
  flex-shrink: 0;
  height: 100%;
  flex-direction: column;
  align-items: center;
  margin: 0;
}

.balm-sidebar-toolbar__items {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--Spacing-spacing-5);
  list-style-type: none;
  margin: 0;
  padding: 0;
  padding-bottom: var(--Spacing-spacing-5); // Space before tour icon
}

.tour-icon-btn {
  margin-top: auto;
  margin-bottom: var(--Spacing-spacing-4);
  padding: var(--Spacing-spacing-2);
  background: transparent;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: var(--Neutral-UI-neutral-ui-20);
  }

  &:focus-visible {
    outline: 2px solid var(--Color-Primary-surface);
    outline-offset: 2px;
  }

  // Dark mode: change icon stroke to white
  :deep(svg path) {
    stroke: var(--Neutral-UI-neutral-ui-100);
  }
}

.tour-badge {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 6px;
  height: 6px;
  background-color: var(--Light-Scheme-Primary-primary-color-3);
  border-radius: 50%;
}
</style>
